/**
 * Utility functions for copying content to clipboard
 */

/**
 * Extracts clean text from markdown content, removing reference numbers and formatting
 * @param content - The markdown content to process
 * @returns Clean text without reference numbers or markdown formatting
 */
export const extractCleanText = (content: string): string => {
    if (!content) return "";

    // Remove references containing only 8-char alphanumeric tokens, e.g. [abc12345] or [abc12345, def67890]
    const cleanText = content.replace(
        /\[(?:[a-zA-Z0-9]{8})(?:,\s*(?:[a-zA-Z0-9]{8}))*\](?!\()/g,
        "",
    );

    return cleanText;
};

/**
 * Escapes HTML special characters in a string
 */
const escapeHtml = (value: string): string =>
    value
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");

/**
 * Converts a subset of Markdown to simple HTML while removing internal reference IDs.
 * Preserves paragraphs, headings, bold/italic, inline code, blockquotes and lists.
 */
export const extractBasicHtml = (content: string): string => {
    if (!content) return "";

    // Remove bracketed references where each token is exactly 8 alphanumeric chars (not markdown links)
    const withoutRefs = content.replace(
        /\[(?:[a-zA-Z0-9]{8})(?:,\s*(?:[a-zA-Z0-9]{8}))*\](?!\()/g,
        "",
    );

    const lines = withoutRefs.split("\n");
    const htmlParts: string[] = [];

    let inUl = false;
    let inOl = false;
    let inCode = false;
    let codeBuffer: string[] = [];
    let currentParagraph: string[] = [];

    const flushParagraph = () => {
        if (currentParagraph.length) {
            const text = currentParagraph.join(" ").trim();
            if (text) htmlParts.push(`<p>${formatInline(text)}</p>`);
            currentParagraph = [];
        }
    };

    const openUl = () => {
        if (!inUl) {
            flushParagraph();
            if (inOl) {
                htmlParts.push("</ol>");
                inOl = false;
            }
            htmlParts.push("<ul>");
            inUl = true;
        }
    };

    const openOl = () => {
        if (!inOl) {
            flushParagraph();
            if (inUl) {
                htmlParts.push("</ul>");
                inUl = false;
            }
            htmlParts.push("<ol>");
            inOl = true;
        }
    };

    const closeLists = () => {
        if (inUl) {
            htmlParts.push("</ul>");
            inUl = false;
        }
        if (inOl) {
            htmlParts.push("</ol>");
            inOl = false;
        }
    };

    const formatInline = (raw: string): string => {
        // Escape first
        let s = escapeHtml(raw);
        // Bold / Italic
        s = s.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
        s = s.replace(/__(.+?)__/g, "<strong>$1</strong>");
        s = s.replace(/\*(.+?)\*/g, "<em>$1</em>");
        s = s.replace(/_(.+?)_/g, "<em>$1</em>");
        // Inline code
        s = s.replace(/`([^`]+)`/g, "<code>$1</code>");
        // Links: keep clickable link
        s = s.replace(
            /\[([^\]]+)\]\(([^)]+)\)/g,
            '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>',
        );
        return s;
    };

    for (const line of lines) {
        // Handle fenced code blocks
        if (!inCode && /^```/.test(line)) {
            closeLists();
            flushParagraph();
            inCode = true;
            codeBuffer = [];
            continue;
        }
        if (inCode) {
            if (/^```/.test(line)) {
                // Close code block
                const codeHtml = `<pre><code>${escapeHtml(codeBuffer.join("\n"))}</code></pre>`;
                htmlParts.push(codeHtml);
                inCode = false;
                codeBuffer = [];
            } else {
                codeBuffer.push(line);
            }
            continue;
        }

        // Blank line => paragraph break
        if (/^\s*$/.test(line)) {
            closeLists();
            flushParagraph();
            continue;
        }

        // Headings
        const headingMatch = line.match(/^(#{1,6})\s+(.*)$/);
        if (headingMatch) {
            const level = headingMatch[1].length;
            const text = headingMatch[2];
            closeLists();
            flushParagraph();
            htmlParts.push(`<h${level}>${formatInline(text)}</h${level}>`);
            continue;
        }

        // Blockquote
        const bqMatch = line.match(/^>\s?(.*)$/);
        if (bqMatch) {
            closeLists();
            flushParagraph();
            htmlParts.push(`<blockquote>${formatInline(bqMatch[1])}</blockquote>`);
            continue;
        }

        // Unordered list
        const ulMatch = line.match(/^\s*[-*]\s+(.*)$/);
        if (ulMatch) {
            openUl();
            htmlParts.push(`<li>${formatInline(ulMatch[1])}</li>`);
            continue;
        }

        // Ordered list
        const olMatch = line.match(/^\s*\d+\.\s+(.*)$/);
        if (olMatch) {
            openOl();
            htmlParts.push(`<li>${formatInline(olMatch[1])}</li>`);
            continue;
        }

        // Regular text -> accumulate into a paragraph
        closeLists();
        currentParagraph.push(line.trim());
    }

    // Flush tail
    if (inCode) {
        const codeHtml = `<pre><code>${escapeHtml(codeBuffer.join("\n"))}</code></pre>`;
        htmlParts.push(codeHtml);
    }
    closeLists();
    flushParagraph();

    return htmlParts.join("");
};

/**
 * Copies text to clipboard using the navigator.clipboard API
 * @param text - The text to copy
 * @returns Promise that resolves when text is copied
 */
export const copyToClipboard = async (text: string): Promise<void> => {
    try {
        await navigator.clipboard.writeText(text);
    } catch (error) {
        console.error("Failed to copy to clipboard:", error);
        // Fallback for older browsers - use writeText with a temporary element
        const textArea = document.createElement("textarea");
        textArea.value = text;
        textArea.style.position = "fixed";
        textArea.style.left = "-999999px";
        textArea.style.top = "-999999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        try {
            // Try modern clipboard API first
            await navigator.clipboard.writeText(text);
        } catch (fallbackError) {
            console.error("Modern clipboard API failed:", fallbackError);
            // Last resort: show user the text to copy manually
            alert(`Please copy this text manually: ${text}`);
        }

        document.body.removeChild(textArea);
    }
};

/**
 * Copies markdown content as both text/plain and text/html to the clipboard.
 * Falls back to text/plain if rich clipboard is not available.
 */
export const copyMarkdownToClipboard = async (content: string): Promise<void> => {
    const text = extractCleanText(content);
    const html = extractBasicHtml(content);

    try {
        if ("ClipboardItem" in window && (navigator.clipboard as unknown as Clipboard).write) {
            const ClipboardItemCtor = (
                window as unknown as {
                    ClipboardItem: new (items: Record<string, Blob>) => ClipboardItem;
                }
            ).ClipboardItem;
            const item = new ClipboardItemCtor({
                "text/plain": new Blob([text], { type: "text/plain" }),
                "text/html": new Blob([html], { type: "text/html" }),
            });
            await (navigator.clipboard as unknown as Clipboard).write([item]);
            return;
        }
    } catch (error) {
        // If rich clipboard fails, fall back to plain text
        console.error("Rich clipboard write failed, falling back to text:", error);
    }

    await copyToClipboard(text);
};
